/**
 * DOM Slider - ES6 Module Version
 * Slide DOM elements up and down with customizable options
 */

class DomSlider {
  constructor() {
    this.defaultOptions = {
      duration: 400,
      easing: 'ease-in-out',
      display: 'block'
    };
  }

  /**
   * Get the natural height of an element
   * @param {HTMLElement} element - The element to measure
   * @returns {number} - The natural height in pixels
   */
  _getNaturalHeight(element) {
    const originalDisplay = element.style.display;
    const originalHeight = element.style.height;
    const originalMaxHeight = element.style.maxHeight;
    const originalOverflow = element.style.overflow;
    
    // Temporarily show element to measure height
    element.style.display = 'block';
    element.style.height = 'auto';
    element.style.maxHeight = 'none';
    element.style.overflow = 'visible';
    
    const height = element.offsetHeight;
    
    // Restore original styles
    element.style.display = originalDisplay;
    element.style.height = originalHeight;
    element.style.maxHeight = originalMaxHeight;
    element.style.overflow = originalOverflow;
    
    return height;
  }

  /**
   * Slide down an element (show)
   * @param {HTMLElement|string} element - Element or selector
   * @param {Object} options - Slide options
   * @returns {Promise}
   */
  slideDown(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (!el) return Promise.reject(new Error('Element not found'));

    const opts = { ...this.defaultOptions, ...options };
    const naturalHeight = this._getNaturalHeight(el);
    
    return new Promise((resolve) => {
      // Set initial state
      el.style.display = opts.display;
      el.style.height = '0px';
      el.style.overflow = 'hidden';
      el.style.transition = `height ${opts.duration}ms ${opts.easing}`;
      
      // Force reflow
      el.offsetHeight;
      
      // Start slide down
      el.style.height = `${naturalHeight}px`;
      
      // Clean up and resolve when animation completes
      setTimeout(() => {
        el.style.height = '';
        el.style.overflow = '';
        el.style.transition = '';
        resolve(el);
      }, opts.duration);
    });
  }

  /**
   * Slide up an element (hide)
   * @param {HTMLElement|string} element - Element or selector
   * @param {Object} options - Slide options
   * @returns {Promise}
   */
  slideUp(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (!el) return Promise.reject(new Error('Element not found'));

    const opts = { ...this.defaultOptions, ...options };
    const currentHeight = el.offsetHeight;
    
    return new Promise((resolve) => {
      // Set initial state
      el.style.height = `${currentHeight}px`;
      el.style.overflow = 'hidden';
      el.style.transition = `height ${opts.duration}ms ${opts.easing}`;
      
      // Force reflow
      el.offsetHeight;
      
      // Start slide up
      el.style.height = '0px';
      
      // Hide element and resolve when animation completes
      setTimeout(() => {
        el.style.display = 'none';
        el.style.height = '';
        el.style.overflow = '';
        el.style.transition = '';
        resolve(el);
      }, opts.duration);
    });
  }

  /**
   * Toggle slide up/down based on current visibility
   * @param {HTMLElement|string} element - Element or selector
   * @param {Object} options - Slide options
   * @returns {Promise}
   */
  slideToggle(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (!el) return Promise.reject(new Error('Element not found'));

    const isVisible = el.offsetHeight > 0 && el.style.display !== 'none';
    
    if (isVisible) {
      return this.slideUp(el, options);
    } else {
      return this.slideDown(el, options);
    }
  }


}

// Create instance
const domSlider = new DomSlider();

// Export both the class and a default instance
export { DomSlider };
export default domSlider;